import { useEffect, useState } from 'react'
import {
  hasInstallPrompt, isIos, isMobileDevice, isTablet, onInstallAvailability,
  shouldGateToPwa, triggerInstall,
} from '../lib/platform'
import { Button } from './ui'
import { Logo } from './Logo'
import './mobile-gate.css'

/* Blocks the touch-device browser entirely. On a phone or tablet the app is
 * only usable as an installed PWA, so this screen replaces the site rather
 * than sitting over it - there is deliberately no dismiss. */
export default function MobileGate() {
  const [gated, setGated] = useState(shouldGateToPwa)
  const [canPrompt, setCanPrompt] = useState(hasInstallPrompt)
  const [installing, setInstalling] = useState(false)
  const [dismissedPrompt, setDismissedPrompt] = useState(false)
  const ios = isIos()
  const tablet = isTablet()
  const mobile = isMobileDevice()
  const deviceWord = !mobile ? 'computer' : tablet ? 'tablet' : 'phone'

  // Desktop browsers that cannot install a PWA: Firefox has no support at
  // all, and Safari before 17 has no "Add to Dock". Rather than leave those
  // people staring at a screen with no way forward, name the browsers that
  // do work.
  const ua = navigator.userAgent
  const desktopNoInstall = !mobile && !canPrompt && /Firefox\//.test(ua)

  useEffect(() => onInstallAvailability(setCanPrompt), [])

  // A user can install and then be launched standalone without a reload.
  useEffect(() => {
    const recheck = () => setGated(shouldGateToPwa())
    const mq = window.matchMedia('(display-mode: standalone)')
    mq.addEventListener('change', recheck)
    window.addEventListener('appinstalled', recheck)
    return () => {
      mq.removeEventListener('change', recheck)
      window.removeEventListener('appinstalled', recheck)
    }
  }, [])

  if (!gated) return null

  async function onInstall() {
    setInstalling(true)
    const outcome = await triggerInstall()
    setInstalling(false)
    if (outcome === 'dismissed') setDismissedPrompt(true)
  }

  return (
    <div className="gate" role="dialog" aria-modal="true" aria-labelledby="gate-title">
      <div className="gate__card">
        <div className="gate__logo"><Logo /></div>

        <div className="gate__art" aria-hidden>
          <svg viewBox="0 0 120 120" width="104" height="104" fill="none"
               stroke="currentColor" strokeWidth="2.4"
               strokeLinecap="round" strokeLinejoin="round">
            <rect x="34" y="12" width="52" height="96" rx="9" />
            <path d="M52 22h16" />
            <circle cx="60" cy="96" r="3.2" />
            <path d="M60 46v26" />
            <path d="m50 62 10 10 10-10" />
          </svg>
        </div>

        <h1 id="gate-title" className="gate__title">Install the app to continue</h1>
        <p className="gate__body">
          On a {deviceWord}, SSM Zambia runs as an installed app. It works offline at the
          site, loads faster on weak networks, and keeps you signed in.
        </p>

        {desktopNoInstall ? (
          <ol className="gate__steps">
            <li>
              This browser cannot install apps. Open <strong>www.ssmzambia.com</strong> in
              <strong> Chrome</strong> or <strong>Microsoft Edge</strong>.
            </li>
            <li>Choose <strong>Install</strong> from the address bar or the browser menu.</li>
            <li>Open SSM Zambia from your desktop or taskbar.</li>
          </ol>
        ) : !mobile && !canPrompt ? (
          <ol className="gate__steps">
            <li>Look for the <strong>install icon</strong> at the right of the address bar.</li>
            <li>Or open the browser menu and choose <strong>Install SSM Zambia</strong>.</li>
            <li>Open it from your desktop or taskbar.</li>
          </ol>
        ) : ios ? (
          <ol className="gate__steps">
            <li>
              Tap the <strong>Share</strong> button
              <span className="gate__inline-icon" aria-hidden>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="1.9"
                     strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 15V4" /><path d="m8 8 4-4 4 4" />
                  <path d="M5 12v6.5A1.5 1.5 0 0 0 6.5 20h11a1.5 1.5 0 0 0 1.5-1.5V12" />
                </svg>
              </span>
              {tablet ? ' in the toolbar at the top of Safari.' : ' at the bottom of Safari.'}
            </li>
            <li>Scroll and choose <strong>Add to Home Screen</strong>.</li>
            <li>Tap <strong>Add</strong>, then open SSM Zambia from your home screen.</li>
          </ol>
        ) : canPrompt ? (
          <>
            <Button size="lg" block loading={installing} onClick={onInstall}>
              Install SSM Zambia
            </Button>
            {dismissedPrompt && (
              <p className="gate__note">
                Installation was cancelled. Tap the button again, or use your
                browser menu and choose <strong>Install app</strong>.
              </p>
            )}
          </>
        ) : (
          <ol className="gate__steps">
            <li>Open your browser menu (<strong>⋮</strong>, top-right).</li>
            <li>Choose <strong>Install app</strong> or <strong>Add to Home screen</strong>.</li>
            <li>Open SSM Zambia from your home screen.</li>
          </ol>
        )}

        {/* Android users can take the native app instead of the PWA - same
            backend, same account. Not offered on iOS, where an APK is
            meaningless. */}
        {!ios && (
          <p className="gate__foot">
            Prefer the full Android app?{' '}
            <a href="/downloads/ssm-zambia.apk" download style={{ color: 'inherit', fontWeight: 600 }}>
              Download the APK
            </a>{' '}
            and install it instead.
          </p>
        )}
        <p className="gate__foot">
          {mobile
            ? 'Installing takes a few seconds and only has to be done once.'
            : 'Installed once, SSM Zambia opens from your desktop like any other program.'}
        </p>
      </div>
    </div>
  )
}
